/**
 * @fileoverview Tooltip component for hover information.
 * Built on Radix UI Tooltip with custom styling.
 * @module components/ui/tooltip
 */

"use client"

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

/**
 * Provider for tooltip delay and skip settings.
 * Re-exports Radix UI Tooltip.Provider.
 */
const TooltipProvider = TooltipPrimitive.Provider

/**
 * Root tooltip component. Controls open/closed state.
 * Re-exports Radix UI Tooltip.Root.
 */
const Tooltip = TooltipPrimitive.Root

/**
 * Element that triggers tooltip on hover.
 * Re-exports Radix UI Tooltip.Trigger.
 */
const TooltipTrigger = TooltipPrimitive.Trigger

/**
 * Tooltip content popup.
 * @param props - Radix TooltipContent props
 * @param props.sideOffset - Distance from trigger element
 * @param ref - Forwarded ref to the content element
 */
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]",
      className
    )}
    data-testid="tooltip-content"
    {...props}
  />
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
